/**
 * The one place the whole app agrees on "which underlying is being tracked".
 *
 * Selection lives in storage (see stableTabStorage) and is broadcast via the
 * `underlyingChanged` / `stableDataFetched` window events. The floating regime
 * badge and the IV/Vol panel both follow it. Any page that lets the user pick a
 * symbol should call `broadcastUnderlyingChange()` so every cross-page widget
 * re-targets -- previously only DataTable dispatched the event, so switching the
 * symbol elsewhere left the badge stale.
 */
import { useEffect, useState } from 'react';
import { canonicalUnderlyingKey } from './underlyingSymbolMatch';
import {
  isStableDataSourceSelected,
  readStablePrimarySessionFirst,
  setStableTabSelection,
} from './stableTabStorage';

export const UNDERLYING_CHANGED_EVENT = 'underlyingChanged';

/** Set the tracked underlying app-wide and notify every cross-page widget. */
export function broadcastUnderlyingChange(symbol: string): void {
  const sym = String(symbol ?? '').trim();
  if (!sym) return;
  try {
    if (isStableDataSourceSelected()) {
      setStableTabSelection([sym]);
    } else if (typeof localStorage !== 'undefined') {
      localStorage.setItem('mmec_selected_underlyings', JSON.stringify([sym]));
      localStorage.setItem('mmec_selected_underlying', sym);
    }
  } catch {
    /* private mode / quota */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(UNDERLYING_CHANGED_EVENT, {
        detail: { underlying: sym, underlyings: [sym] },
      }),
    );
  } catch {
    /* SSR / no window */
  }
}

/** The currently-tracked underlying (canonicalized), live-updated across pages. */
export function useActiveUnderlying(fallback = 'SPY'): string {
  const read = () => {
    const raw = readStablePrimarySessionFirst() || fallback;
    return canonicalUnderlyingKey(raw) || raw.toUpperCase();
  };
  const [sym, setSym] = useState<string>(read);

  useEffect(() => {
    const update = () => setSym(read());
    window.addEventListener(UNDERLYING_CHANGED_EVENT, update as EventListener);
    window.addEventListener('stableDataFetched', update as EventListener);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(UNDERLYING_CHANGED_EVENT, update as EventListener);
      window.removeEventListener('stableDataFetched', update as EventListener);
      window.removeEventListener('storage', update);
    };
  }, []);

  return sym;
}
